import { useEffect, useMemo } from 'react';

/**
 * Meta znacky a strukturovana data.
 *
 * DULEZITE OMEZENI: vyloha je jednostrankova aplikace bez serveroveho
 * vykreslovani. Google si JavaScript spusti a znacky uvidi, ale roboti
 * socialnich siti (Facebook, LinkedIn, Slack) vetsinou ne – ti ctou jen
 * to, co prijde v prvni odpovedi. Sdileny odkaz na produkt proto ukaze
 * nahled z `index.html`, ne z produktu.
 *
 * Vyresit to jde jedine predvykreslenim nebo SSR. Tohle je zaklad, ktery
 * dava smysl mit tak jako tak – ne nahrada za nej.
 */

/**
 * Popisek, kdyz ho stranka nema.
 *
 * Znacku je lepsi nechat s obecnym textem nez ji smazat: bez ni si
 * vyhledavac vymysli vlastni uryvek a socialni site ukazou prazdno.
 */
const DEFAULT_DESCRIPTION =
  'Krmivo a chovatelské potřeby pro psy, kočky a další mazlíčky. Doprava do druhého dne.';

/**
 * Nahled pri sdileni, kdyz stranka vlastni obrazek nema.
 *
 * Odkaz bez obrazku dostane na socialni siti jen sedy obdelnik a proklik
 * na nej je radove horsi. Logo neni idealni nahled, ale je lepsi nez nic.
 */
const FALLBACK_IMAGE = '/logo.png';

/**
 * Parametry, ktere delaji jinou STRANKU.
 *
 * Katalog je jedina adresa s parametry `hledat/kategorie/razeni/skladem/znacka`.
 * Kombinace filtru davaji tomutez zbozi kombinatoricky pocet adres; kdyz se
 * kazda z nich prohlasi za kanonickou, rozmelni se sila jedne stranky mezi
 * stovky duplikatu a robot spali crawl budget na necem, co uz videl.
 *
 * `kategorie` zustava proto, ze je to jedina adresa, kterou dnes kategorie ma –
 * a je i v sitemape. Vsechno ostatni je jen jiny pohled na tytez veci.
 */
const CANONICAL_PARAMS: readonly string[] = ['kategorie'];

/** Cesta bez filtracnich parametru – zaklad pro `canonical` i `og:url`. */
export function canonicalPath(): string {
  const present = new URLSearchParams(window.location.search);
  const kept = new URLSearchParams();

  for (const key of CANONICAL_PARAMS) {
    const value = present.get(key);
    if (value !== null && value.trim() !== '') kept.set(key, value);
  }

  const query = kept.toString();
  return query === '' ? window.location.pathname : `${window.location.pathname}?${query}`;
}

export interface PageMeta {
  readonly title: string;
  readonly description?: string;
  /** Absolutni adresa obrazku pro nahled pri sdileni. */
  readonly imageUrl?: string | null;
  /** `product` u detailu, jinak `website`. */
  readonly type?: 'website' | 'product';
  /** Ma se stranka indexovat? Kosik a pokladna ne. */
  readonly index?: boolean;
}

export function usePageMeta(meta: PageMeta | null): void {
  useSiteJsonLd();

  useEffect(() => {
    if (meta === null) return;

    const description =
      meta.description !== undefined && meta.description.trim() !== ''
        ? meta.description
        : DEFAULT_DESCRIPTION;

    document.title = meta.title;
    setMeta('name', 'description', description);
    // `robots` se nastavuje vzdy, i kdyz se ma indexovat – jinak by po
    // odchodu z kosiku zustalo `noindex` viset na dalsi strance.
    setMeta('name', 'robots', meta.index === false ? 'noindex, follow' : 'index, follow');

    // Kanonicka adresa a `og:url` musi rikat TOTEZ. Kdyz si odporuji – a do
    // ted si odporovaly, canonical mel parametry a og:url ne – vybere si
    // vyhledavac sam a obvykle hur, nez kdyby dostal jednu odpoved.
    const canonical = absolute(canonicalPath());
    setLink('canonical', canonical);

    setMeta('property', 'og:title', meta.title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', meta.type ?? 'website');
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', meta.imageUrl ?? absolute(FALLBACK_IMAGE));
    setMeta('name', 'twitter:card', meta.imageUrl == null ? 'summary' : 'summary_large_image');
  }, [meta]);
}

/**
 * Strukturovana data (JSON-LD).
 *
 * Google z nich sklada bohaty vysledek – cenu a dostupnost primo ve
 * vyhledavani. Musi popisovat TOTEZ, co je na strance videt; rozchazet
 * se s ni znamena riskovat penalizaci, ne lepsi umisteni.
 *
 * `id` odlisuje bloky, ktere na strance zijí soubezne: udaje o obchodu
 * vklada `Layout` na kazdou stranku, produkt a drobecky si pridava detail.
 * Bez vlastniho `id` by si dva bloky navzajem prepisovaly misto v hlavicce.
 */
export type JsonLd = Readonly<Record<string, unknown>>;

export function useJsonLd(
  data: JsonLd | readonly JsonLd[] | null,
  id = 'zooshopik-jsonld',
): void {
  useEffect(() => {
    document.getElementById(id)?.remove();
    if (data === null) return;

    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.append(script);

    return () => {
      document.getElementById(id)?.remove();
    };
  }, [data, id]);
}

/**
 * Udaje o obchodu (JSON-LD `Store`).
 *
 * Vklada se odsud, a ne z `Layout`, protoze ma byt na kazde indexovatelne
 * strance a `usePageMeta` je jedine misto, kterym kazda z nich prochazi.
 * Vlastni `id` skriptu ho drzi stranou od produktovych dat – dva bloky by si
 * jinak prepisovaly totez misto v hlavicce.
 *
 * Jsou tu jen udaje, ktere jsou na strance opravdu videt (telefon v servisni
 * liste, logo). Adresa provozovny ani ICO ve vyloze nikde nejsou, takze se
 * sem nedopisuji: vymysleny udaj ve strukturovanych datech je horsi nez
 * chybejici – Google ho porovnava s obsahem stranky.
 */
const SITE_JSON_LD_ID = 'zooshopik-jsonld-site';
/** Tentyz telefon, jaky je videt v servisni liste (components/Layout.tsx). */
const SHOP_PHONE = '+420739626040';

function useSiteJsonLd(): void {
  const data = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Store',
      name: 'Super Krmivo',
      description: DEFAULT_DESCRIPTION,
      url: absolute('/'),
      logo: absolute('/logo.png'),
      image: absolute('/logo.png'),
      telephone: SHOP_PHONE,
    }),
    [],
  );

  useJsonLd(data, SITE_JSON_LD_ID);
}

/** Absolutni adresa – relativni cesta v `og:url` ani `canonical` neplatí. */
export function absolute(path: string): string {
  return new URL(path, window.location.origin).href;
}

function setMeta(attribute: 'name' | 'property', key: string, value: string | undefined): void {
  const selector = `meta[${attribute}="${key}"]`;
  const existing = document.head.querySelector(selector);

  if (value === undefined || value === '') {
    existing?.remove();
    return;
  }

  const tag = existing ?? document.createElement('meta');
  tag.setAttribute(attribute, key);
  tag.setAttribute('content', value);
  if (existing === null) document.head.append(tag);
}

function setLink(rel: string, href: string): void {
  const existing = document.head.querySelector(`link[rel="${rel}"]`);
  const tag = existing ?? document.createElement('link');
  tag.setAttribute('rel', rel);
  tag.setAttribute('href', href);
  if (existing === null) document.head.append(tag);
}
